/**
 * step-test.tsx — Bước 3: chạy thử. 3 câu gợi ý theo mẫu (bấm là gửi vào khung chat thử bên phải), tóm tắt đánh giá
 * 👍 / 👎 từng câu trả lời, các câu chưa đạt kèm gợi ý mục cần sửa. Bước xong khi đã có ít nhất một lượt thử.
 */
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight, MessageSquareText, ThumbsDown, ThumbsUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ChatTurn } from '@/components/ai/bot-test-chat'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import type { WizardTemplate } from './wizard-templates'
import { botDetailPath, dislikeHints, ratingSummary } from './wizard-lib'

export function StepTest({ botId, botName, template, turns, onAsk, onGoKnowledge, onBack, onNext }: {
  botId: string
  botName: string
  template: WizardTemplate
  turns: ChatTurn[]
  onAsk: (text: string) => void
  onGoKnowledge: () => void
  onBack: () => void
  onNext: () => void
}) {
  const { t } = useTranslation('aiWizard')
  const sum = ratingSummary(turns)
  const disliked = turns.filter((x) => !x.error && x.rating === 'down')

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-medium">{t('test.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('test.intro', { botName })}
        </p>
      </div>

      <section className="space-y-2">
        <h3 className="text-sm font-medium">{t('test.suggested')}</h3>
        <div className="grid gap-2">
          {template.testQuestions.map((q) => (
            <button
              key={q} type="button" onClick={() => onAsk(q)}
              className="flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm hover:border-primary/60 hover:bg-muted/40"
            >
              <MessageSquareText className="h-4 w-4 shrink-0 text-primary" /> {q}
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-2 rounded-xl border p-4">
        <h3 className="text-sm font-medium">{t('test.results')}</h3>
        {sum.total === 0 ? (
          <p className="text-xs text-muted-foreground">{t('test.none')}</p>
        ) : (
          <p className="flex flex-wrap items-center gap-3 text-sm">
            <span>{t('test.turns', { count: sum.total })}</span>
            <span className="flex items-center gap-1 text-success"><ThumbsUp className="h-3.5 w-3.5" /> {t('test.passed', { count: sum.up })}</span>
            <span className="flex items-center gap-1 text-destructive"><ThumbsDown className="h-3.5 w-3.5" /> {t('test.failed', { count: sum.down })}</span>
            {sum.unrated > 0 && <span className="text-muted-foreground">{t('test.unrated', { count: sum.unrated })}</span>}
          </p>
        )}
        {disliked.length > 0 && (
          <ul className="space-y-2 pt-1">
            {disliked.map((d) => (
              <li key={d.key} className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs">
                <p className="font-medium">"{d.customerText}"</p>
                <p className="mt-1 text-muted-foreground">
                  {t('test.shouldFix')}{' '}
                  {dislikeHints({ sourceCount: d.sources.length }).map((h, i) => (
                    <span key={h.key}>
                      {i > 0 && ' · '}
                      {h.key === 'kien-thuc'
                        ? <button type="button" className="text-primary hover:underline" onClick={onGoKnowledge}>{h.label}</button>
                        : <OpenInNewTab to={botDetailPath(botId, 'vai-tro')}>{h.label}</OpenInNewTab>}
                    </span>
                  ))}
                </p>
              </li>
            ))}
          </ul>
        )}
        {sum.down > 0 && (
          <p className="text-[11px] text-muted-foreground">
            {t('test.fixHint', { botName })}
          </p>
        )}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4" /> {t('common:actions.back')}</Button>
        <div className="flex items-center gap-2">
          {sum.total === 0 && <span className="text-xs text-muted-foreground">{t('test.tryOne')}</span>}
          <Button onClick={onNext} disabled={sum.total === 0}>{t('test.next')} <ArrowRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  )
}
